/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { RefCallback } from "react"
import { useLayoutEffect, useState } from "react"

import type { ValueOf } from "./types"

export const OverflowAxis = {
    Inline: "inline",
    Block: "block"
} as const
export type OverflowAxis = ValueOf<typeof OverflowAxis>

interface Options {
    /**
     * Axis on which to measure overflow.
     *
     * @default OverflowAxis.Inline
     */
    axis?: OverflowAxis
    /**
     * Disables overflow measurement and reports `false`.
     *
     * @default false
     */
    disabled?: boolean
    /**
     * Re-measures when this value changes.
     *
     * ResizeObserver does not detect content changes when the box size stays
     * the same, so pass the value that affects the measured content.
     */
    watch?: unknown
}

interface Result {
    /** Ref callback for the element whose content is being measured. */
    contentRef: RefCallback<HTMLElement>
    /** Ref callback for the box to measure against; defaults to `contentRef`. */
    containerRef: RefCallback<HTMLElement>
    /** Whether the content exceeds the available space. */
    isOverflowing: boolean
}

/**
 * Reports whether an element's content exceeds the available space.
 *
 * By default, the content element is measured against itself. Pass
 * `containerRef` as well to measure the content against a separate, stable
 * container. This is useful when the UI changes in response to the result,
 * such as adding a sibling when the content overflows.
 *
 * The measurement is re-run when the observed elements resize or when `watch`
 * changes.
 *
 * @param options - Measurement options. See {@link Options}.
 * @returns Refs for the content and optional container, and the current
 *   overflow state.
 *
 * @example
 * const { contentRef, isOverflowing } = useIsOverflowing({ watch: text })
 * return <span ref={contentRef}>{text}</span>
 *
 * @example
 * const { containerRef, contentRef, isOverflowing } = useIsOverflowing({ watch: text })
 * return (
 *     <span ref={containerRef}>
 *         <span ref={contentRef}>{text}</span>
 *         {isOverflowing && <span aria-hidden>{text}</span>}
 *     </span>
 * )
 */
export const useIsOverflowing = ({ axis = OverflowAxis.Inline, disabled = false, watch }: Options = {}): Result => {
    const [content, setContent] = useState<HTMLElement | null>(null)
    const [container, setContainer] = useState<HTMLElement | null>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)

    /*
     * `watch` is a re-measure trigger. ResizeObserver only reacts to size
     * changes, so content changes that do not resize the box need an explicit
     * trigger.
     */
    // biome-ignore lint/correctness/useExhaustiveDependencies: watch is the re-measure trigger, see above
    useLayoutEffect(() => {
        if (disabled || !content) {
            setIsOverflowing(false)
            return
        }

        /*
         * Measure against the container when provided. Keeping the container separate
         * prevents UI changes triggered by `isOverflowing` from changing the reference
         * box and causing the result to oscillate.
         */
        const box = container ?? content

        const measure = () =>
            setIsOverflowing(
                axis === OverflowAxis.Block
                    ? content.scrollHeight > box.clientHeight
                    : content.scrollWidth > box.clientWidth
            )

        measure()

        const observer = new ResizeObserver(measure)
        observer.observe(box)
        if (box !== content) {
            observer.observe(content)
        }

        return () => observer.disconnect()
    }, [axis, container, content, disabled, watch])

    return { contentRef: setContent, containerRef: setContainer, isOverflowing }
}
